import React, { useEffect, useState } from "react";
import { PageHeader, StatusBadge, Modal, Toast, EmptyState, PasswordInput, ExportButtons } from "@/components/Layout";
import api from "@/lib/api";
import Select from "@/components/Select";

export default function Licenses() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [billingFrequencies, setBillingFrequencies] = useState([]);
  const [adding, setAdding] = useState(null);
  const [addingClient, setAddingClient] = useState(null);
  const [pricing, setPricing] = useState(null); // {plan_key: price_ron} draft, deschis când != null
  const [renewing, setRenewing] = useState(null); // {id, license_key, valid_to, unit_price_ron, bill_per_employment_count}
  const [settingAdminFor, setSettingAdminFor] = useState(null); // {cui, company_name, admin_username, admin_password, admin_first_name, admin_last_name, admin_email}
  const [msg, setMsg] = useState({text:"",type:"info"});

  const load = async () => {
    const [r,c,m,p,bf] = await Promise.all([
      api.get("/licenses"), api.get("/clients"), api.get("/licenses/modules"), api.get("/licenses/plans"),
      api.get("/licenses/billing-frequencies"),
    ]);
    setRows(r.data); setClients(c.data); setModules(m.data); setPlans(p.data); setBillingFrequencies(bf.data);
  };
  useEffect(() => { load(); }, []);

  const newClient = () => setAddingClient({company_name:"", cui:"", contact_email:"", notes:"",
    employer_address:"", employer_phone:"", employer_email:"", legal_representative_name:"", legal_representative_title:"",
    admin_username:"", admin_password:"", admin_first_name:"", admin_last_name:"", admin_email:""});
  const saveClient = async () => {
    if (!addingClient.company_name?.trim()) return setMsg({text:"Compania / Angajatorul este obligatoriu",type:"error"});
    if (!addingClient.cui?.trim()) return setMsg({text:"CUI-ul companiei este obligatoriu",type:"error"});
    if (addingClient.admin_username?.trim()) {
      if (!addingClient.admin_password || addingClient.admin_password.length < 12)
        return setMsg({text:"Parola contului de admin trebuie să aibă minim 12 caractere",type:"error"});
      if (!addingClient.admin_first_name?.trim() || !addingClient.admin_last_name?.trim())
        return setMsg({text:"Nume și prenume obligatorii pentru contul de admin",type:"error"});
    }
    try {
      const r = await api.post("/clients", addingClient);
      setAddingClient(null); load();
      const provisioned = r.data?.admin_provisioned;
      setMsg({text: provisioned ? `Client definit. Cont Admin provizionat: ${provisioned.username}` : "Client definit", type:"success"});
      setTimeout(()=>setMsg({text:""}),3000);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const openPricing = async () => {
    const r = await api.get("/licenses/plan-prices");
    const draft = {};
    r.data.forEach(p => { draft[p.plan_key] = p.price_ron; });
    setPricing(draft);
  };
  const savePricing = async () => {
    try {
      for (const [plan_key, price_ron] of Object.entries(pricing)) {
        await api.put("/licenses/plan-prices", { plan_key, price_ron: parseFloat(price_ron) || 0 });
      }
      setPricing(null); load();
      setMsg({text:"Prețuri salvate",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const save = async () => {
    if (!adding.cui?.trim()) return setMsg({text:"Alege clientul căruia îi aloci licența",type:"error"});
    if (!adding.bill_per_employment_count && (adding.manual_price_ron === "" || adding.manual_price_ron == null))
      return setMsg({text:"Introdu manual prețul licenței (facturare per raport neactivată)",type:"error"});
    try {
      const payload = {...adding, manual_price_ron: adding.bill_per_employment_count ? null : (parseFloat(adding.manual_price_ron) || 0)};
      await api.post("/licenses", payload);
      setAdding(null); load();
      setMsg({text: "Licență emisă", type:"success"});
      setTimeout(()=>setMsg({text:""}),3000);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };
  const action = async (id, act) => {
    await api.post(`/licenses/${id}/${act}`); load();
  };
  const generateKit = async (id, flavor="ubuntu") => {
    try {
      const r = await api.post(`/licenses/${id}/generate-kit`, {flavor}, {responseType:"blob"});
      if (r.data.type === "application/json") {
        const text = await r.data.text();
        const parsed = JSON.parse(text);
        setMsg({text: parsed.message || "Ok", type:"info"});
      } else {
        const disposition = r.headers["content-disposition"] || "";
        const match = disposition.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1] : `onomad-kit-${flavor}.tar.gz`;
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = filename; document.body.appendChild(a); a.click();
        a.remove(); window.URL.revokeObjectURL(url);
        setMsg({text: "Kit descărcat", type:"success"});
      }
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
    setTimeout(()=>setMsg({text:""}),3000);
  };
  const openRenew = (l) => setRenewing({ id: l.id, license_key: l.license_key,
    plan: l.plan, valid_to: l.valid_to, unit_price_ron: l.plan_price_ron, bill_per_employment_count: l.bill_per_employment_count,
    billing_frequency: l.billing_frequency || "monthly",
    billing_anchor_date: l.billing_anchor_date || new Date().toISOString().slice(0,10) });
  const saveRenew = async () => {
    if (renewing.unit_price_ron < 0) return setMsg({text:"Prețul nu poate fi negativ",type:"error"});
    try {
      await api.put(`/licenses/${renewing.id}/renew`, {
        plan: renewing.plan,
        valid_to: renewing.valid_to,
        unit_price_ron: parseFloat(renewing.unit_price_ron) || 0,
        bill_per_employment_count: renewing.bill_per_employment_count,
        billing_frequency: renewing.billing_frequency,
        billing_anchor_date: renewing.billing_anchor_date,
      });
      setRenewing(null); load();
      setMsg({text:"Licență reînnoită",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const openSetAdmin = (client) => setSettingAdminFor({
    cui: client.cui, company_name: client.company_name,
    admin_username: client.admin_username || "", admin_password: "",
    admin_first_name: client.admin_first_name || "", admin_last_name: client.admin_last_name || "",
    admin_email: client.admin_email || "",
  });
  const saveClientAdmin = async () => {
    if (!settingAdminFor.admin_username?.trim()) return setMsg({text:"Username-ul contului de admin este obligatoriu",type:"error"});
    if (!settingAdminFor.admin_password || settingAdminFor.admin_password.length < 12)
      return setMsg({text:"Parola contului de admin trebuie să aibă minim 12 caractere",type:"error"});
    if (!settingAdminFor.admin_first_name?.trim() || !settingAdminFor.admin_last_name?.trim())
      return setMsg({text:"Nume și prenume obligatorii pentru contul de admin",type:"error"});
    try {
      const { cui, ...body } = settingAdminFor;
      await api.put(`/clients/${cui}/admin`, body);
      setSettingAdminFor(null); load();
      setMsg({text:"Cont Admin provizionat", type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const newLic = () => {
    const today = new Date().toISOString().slice(0,10);
    const end = new Date(); end.setFullYear(end.getFullYear()+1);
    setAdding({cui: clients[0]?.cui || "", module_key: modules[0]?.key || "planco",
      plan:"business", valid_from: today, valid_to: end.toISOString().slice(0,10),
      bill_per_employment_count: false, manual_price_ron:"", notes:"",
      billing_frequency:"monthly", billing_anchor_date: today});
  };

  const expiringSoon = rows.filter(l => l.status === "active" && l.days_left != null && l.days_left <= 20);

  return (
    <>
      <PageHeader title="Licențe (Superadmin)" subtitle="Licențiere per-modul"
        actions={<>
          <ExportButtons basePath="/licenses" />
          <button className="btn btn-secondary" onClick={openPricing} data-testid="open-pricing">Prețuri</button>
          <button className="btn btn-secondary" onClick={newClient} data-testid="add-client">+ Client nou</button>
          <button className="btn btn-primary" onClick={newLic} disabled={clients.length===0}
            title={clients.length===0 ? "Definește întâi un client" : undefined} data-testid="add-license">
            + Emite licență
          </button>
        </>} />
      <Toast msg={msg.text} type={msg.type} />

      {expiringSoon.length > 0 && (
        <div className="card" style={{padding:16, marginBottom:16, borderLeft:"3px solid #DC2626", background:"#FEF2F2"}} data-testid="expiring-soon-alert">
          <div style={{fontSize:12,fontWeight:700,color:"#991B1B",textTransform:"uppercase",letterSpacing:"0.05em"}}>
            ⚠ {expiringSoon.length} licențe expiră în cel mult 20 de zile
          </div>
          <div style={{marginTop:8,display:"grid",gap:4}}>
            {expiringSoon.map(l => (
              <div key={l.id} style={{fontSize:13,color:"#334155"}}>
                <b>{l.company_name}</b> (CUI {l.cui}) — {l.module_label} — expiră la {l.valid_to} ({l.days_left} zile)
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"grid",gap:12}}>
        {rows.map(l => (
          <div key={l.id} className="card fade-up" style={{padding:20,
              borderLeft: l.status==="active" ? "3px solid var(--blue-primary)" : "1px solid #E2E8F0"}} data-testid={`license-${l.license_key}`}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:"var(--blue-primary)",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase"}}>{l.module_label}</span>
                  <StatusBadge status={l.status} />
                  <span className="badge badge-approved">{l.plan_label}</span>
                </div>
                <div style={{fontSize:17,fontWeight:700,color:"var(--navy)",marginTop:4}}>{l.company_name} <span style={{fontSize:12,fontWeight:400,color:"#64748B"}}>CUI: {l.cui}</span></div>
                <div style={{fontFamily:"monospace",fontSize:12,color:"#64748B",marginTop:2}}>Cheie: {l.license_key}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginTop:12,fontSize:13,color:"#334155"}}>
                  <div><b>Valabilitate:</b> {l.valid_from} → {l.valid_to}</div>
                  <div><b>Zile rămase:</b> {l.days_left ?? "—"}</div>
                  <div><b>Max rapoarte de muncă:</b> {l.max_employments === -1 ? "Nelimitat" : l.max_employments}</div>
                </div>
                <div style={{marginTop:6,fontSize:12,color:"#334155"}}>
                  <b>Facturare:</b> {l.bill_per_employment_count
                    ? `${l.plan_price_ron} Lei × nr. rapoarte de muncă active+suspendate`
                    : `preț fix, ${l.plan_price_ron} Lei`}
                  {" · "}{l.billing_frequency_label}
                  {l.next_billing_date && ` · următoarea factură: ${l.next_billing_date}`}
                </div>
                {l.notes && <div style={{marginTop:8,fontSize:12,color:"#64748B",fontStyle:"italic"}}>{l.notes}</div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
                {l.status==="active" && <button className="btn btn-secondary" style={{padding:"3px 10px",fontSize:12}} onClick={()=>action(l.id,"suspend")}>Suspendă</button>}
                {l.status==="suspended" && <button className="btn btn-primary" style={{padding:"3px 10px",fontSize:12}} onClick={()=>action(l.id,"reactivate")}>Reactivează</button>}
                <button className="btn btn-secondary" style={{padding:"3px 10px",fontSize:12}}
                  onClick={()=>openRenew(l)} data-testid={`renew-${l.license_key}`}>
                  Reînnoiește / Modifică
                </button>
                <button className={clients.find(c=>c.cui===l.cui)?.admin_username ? "btn btn-secondary" : "btn btn-danger"}
                  style={{padding:"3px 10px",fontSize:12}}
                  onClick={()=>openSetAdmin(clients.find(c=>c.cui===l.cui) || {cui:l.cui, company_name:l.company_name})}
                  data-testid={`set-client-admin-${l.license_key}`}>
                  {clients.find(c=>c.cui===l.cui)?.admin_username
                    ? `Cont Admin: ${clients.find(c=>c.cui===l.cui).admin_username}`
                    : "Cont Admin neconfigurat — setează"}
                </button>
                <button className="btn btn-secondary" style={{padding:"3px 10px",fontSize:12}}
                  onClick={()=>generateKit(l.id,"ubuntu")} data-testid={`generate-kit-ubuntu-${l.license_key}`}>
                  Kit Ubuntu
                </button>
                <button className="btn btn-secondary" disabled
                  style={{padding:"3px 10px",fontSize:12,opacity:0.5,cursor:"not-allowed"}}
                  title="Kit-urile Windows nu sunt încă validate. Folosește kit-ul Ubuntu sau un VM Linux."
                  data-testid={`generate-kit-windows-${l.license_key}`}>
                  Kit Windows — indisponibil
                </button>
                <span style={{fontSize:12,color:"#B45309"}} data-testid={`windows-kit-note-${l.license_key}`}>
                  Kit-urile Windows nu sunt încă validate. Folosește kit-ul Ubuntu sau un VM Linux.
                </span>
              </div>
            </div>
          </div>
        ))}
        {rows.length===0 && (
          <EmptyState text={clients.length===0
            ? 'Niciun client definit încă. Apasă "+ Client nou" ca să începi.'
            : 'Niciun client nu are încă licențe. Apasă "+ Emite licență".'} />
        )}
      </div>

      {adding && (
        <Modal title="Emite licență" onClose={()=>setAdding(null)}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Client</label>
              <Select className="select" value={adding.cui} onChange={e=>setAdding({...adding,cui:e.target.value})} data-testid="lic-client">
                {clients.map(c=><option key={c.cui} value={c.cui}>{c.company_name} (CUI: {c.cui})</option>)}
              </Select>
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Nu găsești clientul? Definește-l întâi cu "+ Client nou".</div>
            </div>
            <div><label className="label label-req">Modul</label>
              <Select className="select" value={adding.module_key} onChange={e=>setAdding({...adding,module_key:e.target.value})} data-testid="lic-module">
                {modules.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
              </Select></div>
            <div><label className="label label-req">Plan</label>
              <Select className="select" value={adding.plan} onChange={e=>setAdding({...adding,plan:e.target.value})} data-testid="lic-plan">
                {plans.map(p=><option key={p.key} value={p.key}>{p.label} (max {p.max_employments===-1?"nelimitat":p.max_employments} rapoarte de muncă · {p.price_ron} Lei)</option>)}
              </Select></div>
            <div className="grid-2">
              <div><label className="label label-req">Valabil de la</label>
                <input className="input" type="date" value={adding.valid_from} onChange={e=>setAdding({...adding,valid_from:e.target.value})} /></div>
              <div><label className="label label-req">Valabil până la</label>
                <input className="input" type="date" value={adding.valid_to} onChange={e=>setAdding({...adding,valid_to:e.target.value})} /></div>
            </div>
            <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
              <input type="checkbox" id="bill-per-empl" checked={adding.bill_per_employment_count}
                onChange={e=>setAdding({...adding,bill_per_employment_count:e.target.checked})}
                data-testid="lic-bill-per-employment" style={{marginTop:3}} />
              <label htmlFor="bill-per-empl" style={{fontSize:13,cursor:"pointer"}}>
                Facturare per raport de muncă<br/>
                <span style={{fontSize:11,color:"#64748B"}}>Bifat: preț plan (din "Prețuri") × nr. rapoarte de muncă active+suspendate ale angajatorului. Nebifat: preț fix, introdus manual mai jos — lista de prețuri e ignorată.</span>
              </label>
            </div>
            {!adding.bill_per_employment_count && (
              <div><label className="label label-req">Preț (Lei)</label>
                <input className="input" type="number" min="0" step="0.01" value={adding.manual_price_ron}
                  onChange={e=>setAdding({...adding,manual_price_ron:e.target.value})} data-testid="lic-manual-price" />
                <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Preț fix pentru această licență, independent de lista de prețuri per plan.</div>
              </div>
            )}
            <div className="grid-2">
              <div><label className="label label-req">Frecvență facturare</label>
                <Select className="select" value={adding.billing_frequency}
                  onChange={e=>setAdding({...adding,billing_frequency:e.target.value})} data-testid="lic-billing-frequency">
                  {billingFrequencies.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
                </Select></div>
              <div><label className="label label-req">Data de facturare</label>
                <input className="input" type="date" value={adding.billing_anchor_date}
                  onChange={e=>setAdding({...adding,billing_anchor_date:e.target.value})} data-testid="lic-billing-anchor" />
                <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Prima dată de facturare — de-aici se calculează recurența (ex: lunar, în fiecare lună pe aceeași zi).</div>
              </div>
            </div>
            <div><label className="label">Note</label>
              <textarea className="textarea" rows={2} value={adding.notes||""} onChange={e=>setAdding({...adding,notes:e.target.value})} /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setAdding(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={save} data-testid="save-license">Emite</button>
          </div>
        </Modal>
      )}

      {addingClient && (
        <Modal title="Client nou" onClose={()=>setAddingClient(null)}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Companie / Angajator</label>
              <input className="input" value={addingClient.company_name}
                onChange={e=>setAddingClient({...addingClient,company_name:e.target.value})} data-testid="client-company" /></div>
            <div><label className="label label-req">CUI Companie / Angajator</label>
              <input className="input" value={addingClient.cui}
                onChange={e=>setAddingClient({...addingClient,cui:e.target.value})} data-testid="client-cui" />
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Identificatorul real al clientului — o dată definit, îi poți aloca oricâte licențe/module fără să-l reintroduci.</div>
            </div>
            <div><label className="label">Email contact (persoana ta de contact la client, pentru licențiere)</label>
              <input className="input" value={addingClient.contact_email||""}
                onChange={e=>setAddingClient({...addingClient,contact_email:e.target.value})} /></div>
            <div><label className="label">Note</label>
              <textarea className="textarea" rows={2} value={addingClient.notes||""}
                onChange={e=>setAddingClient({...addingClient,notes:e.target.value})} /></div>

            <EmployerFields value={addingClient} onChange={setAddingClient} />

            <div style={{borderTop:"1px solid #E2E8F0",paddingTop:12,marginTop:4}}>
              <div style={{fontSize:13,fontWeight:700,color:"var(--navy)"}}>Cont Admin inițial</div>
              <div style={{fontSize:11,color:"#64748B",marginBottom:10}}>
                Poți completa acum sau mai târziu (buton "Cont Admin" pe fiecare licență) — dar
                obligatoriu ÎNAINTE de a genera kitul: instalarea clientului pornește direct cu
                acest cont (Admin+HR), nu-și mai creează propriul cont de superadmin.
              </div>
              <div className="grid-2">
                <div><label className="label">Utilizator</label>
                  <input className="input" value={addingClient.admin_username||""}
                    onChange={e=>setAddingClient({...addingClient,admin_username:e.target.value})} data-testid="client-admin-username" /></div>
                <div><label className="label">Parolă inițială</label>
                  <PasswordInput className="input" autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" value={addingClient.admin_password||""}
                    onChange={e=>setAddingClient({...addingClient,admin_password:e.target.value})} testId="client-admin-password" /></div>
                <div><label className="label">Prenume</label>
                  <input className="input" value={addingClient.admin_first_name||""}
                    onChange={e=>setAddingClient({...addingClient,admin_first_name:e.target.value})} /></div>
                <div><label className="label">Nume</label>
                  <input className="input" value={addingClient.admin_last_name||""}
                    onChange={e=>setAddingClient({...addingClient,admin_last_name:e.target.value})} /></div>
              </div>
              <div style={{marginTop:10}}><label className="label">Email</label>
                <input className="input" value={addingClient.admin_email||""}
                  onChange={e=>setAddingClient({...addingClient,admin_email:e.target.value})} /></div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setAddingClient(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={saveClient} data-testid="save-client">Salvează client</button>
          </div>
        </Modal>
      )}

      {settingAdminFor && (
        <Modal title={`Cont Admin — ${settingAdminFor.company_name}`} onClose={()=>setSettingAdminFor(null)}>
          <div style={{display:"grid",gap:12}}>
            <div style={{fontSize:11,color:"#64748B"}}>
              {settingAdminFor.admin_username
                ? "Înlocuiește contul Admin provizionat pentru acest client — utilă pentru o parolă temporară nouă înainte de a regenera kitul."
                : "Niciun cont Admin provizionat încă — obligatoriu înainte de a genera kitul de instalare pentru acest client."}
            </div>
            <div className="grid-2">
              <div><label className="label label-req">Utilizator</label>
                <input className="input" value={settingAdminFor.admin_username}
                  onChange={e=>setSettingAdminFor({...settingAdminFor,admin_username:e.target.value})} data-testid="set-admin-username" /></div>
              <div><label className="label label-req">Parolă {settingAdminFor.admin_username ? "nouă" : "inițială"}</label>
                <PasswordInput className="input" autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" value={settingAdminFor.admin_password}
                  onChange={e=>setSettingAdminFor({...settingAdminFor,admin_password:e.target.value})} testId="set-admin-password" /></div>
              <div><label className="label label-req">Prenume</label>
                <input className="input" value={settingAdminFor.admin_first_name}
                  onChange={e=>setSettingAdminFor({...settingAdminFor,admin_first_name:e.target.value})} /></div>
              <div><label className="label label-req">Nume</label>
                <input className="input" value={settingAdminFor.admin_last_name}
                  onChange={e=>setSettingAdminFor({...settingAdminFor,admin_last_name:e.target.value})} /></div>
            </div>
            <div><label className="label">Email</label>
              <input className="input" value={settingAdminFor.admin_email}
                onChange={e=>setSettingAdminFor({...settingAdminFor,admin_email:e.target.value})} /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setSettingAdminFor(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={saveClientAdmin} data-testid="save-client-admin">Salvează</button>
          </div>
        </Modal>
      )}

      {renewing && (
        <Modal title={`Reînnoiește / Modifică — ${renewing.license_key}`} onClose={()=>setRenewing(null)}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Tipul licenței (plan)</label>
              <Select className="select" value={renewing.plan} onChange={e=>setRenewing({...renewing,plan:e.target.value})} data-testid="renew-plan">
                {plans.map(p=><option key={p.key} value={p.key}>{p.label} (max {p.max_employments===-1?"nelimitat":p.max_employments} rapoarte de muncă)</option>)}
              </Select>
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Limita de rapoarte de muncă a clientului se schimbă la următoarea sincronizare. Prețul înghețat al licenței rămâne cel de mai jos, până îl modifici.</div></div>
            <div><label className="label label-req">Valabil până la</label>
              <input className="input" type="date" value={renewing.valid_to}
                onChange={e=>setRenewing({...renewing,valid_to:e.target.value})} data-testid="renew-valid-to" /></div>
            <div><label className="label label-req">Preț (Lei)</label>
              <input className="input" type="number" min="0" step="0.01" value={renewing.unit_price_ron}
                onChange={e=>setRenewing({...renewing,unit_price_ron:e.target.value})} data-testid="renew-price" />
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Prețul e înghețat pe această licență — nu se mai schimbă automat dacă modifici ulterior prețul planului din "Prețuri".</div>
            </div>
            <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
              <input type="checkbox" id="renew-bill-per-empl" checked={renewing.bill_per_employment_count}
                onChange={e=>setRenewing({...renewing,bill_per_employment_count:e.target.checked})}
                data-testid="renew-bill-per-employment" style={{marginTop:3}} />
              <label htmlFor="renew-bill-per-empl" style={{fontSize:13,cursor:"pointer"}}>Facturare per raport de muncă</label>
            </div>
            <div className="grid-2">
              <div><label className="label label-req">Frecvență facturare</label>
                <Select className="select" value={renewing.billing_frequency}
                  onChange={e=>setRenewing({...renewing,billing_frequency:e.target.value})} data-testid="renew-billing-frequency">
                  {billingFrequencies.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
                </Select></div>
              <div><label className="label label-req">Data de facturare</label>
                <input className="input" type="date" value={renewing.billing_anchor_date}
                  onChange={e=>setRenewing({...renewing,billing_anchor_date:e.target.value})} data-testid="renew-billing-anchor" /></div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setRenewing(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={saveRenew} data-testid="save-renew">Salvează</button>
          </div>
        </Modal>
      )}

      {pricing && (
        <Modal title="Prețuri per tip de licență" onClose={()=>setPricing(null)}>
          <div style={{display:"grid",gap:12}}>
            {plans.map(p => (
              <div key={p.key} style={{display:"grid",gridTemplateColumns:"1fr 140px",gap:12,alignItems:"center"}}>
                <label className="label" style={{margin:0}}>{p.label}</label>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <input className="input" type="number" min="0" step="0.01"
                    value={pricing[p.key] ?? 0}
                    onChange={e=>setPricing({...pricing, [p.key]: e.target.value})}
                    data-testid={`price-${p.key}`} />
                  <span style={{fontSize:12,color:"#64748B"}}>Lei</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <button className="btn btn-secondary" onClick={()=>setPricing(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={savePricing} data-testid="save-pricing">Salvează</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// Datele angajatorului trimise automat instalării clientului, la activarea
// licenței — Adminul lui găsește angajatorul deja configurat. Folosit și la
// editarea clientului (Lista Angajatori/Clienți).
export function EmployerFields({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div style={{borderTop:"1px solid #E2E8F0",paddingTop:12,marginTop:4}}>
      <div style={{fontSize:13,fontWeight:700,color:"var(--navy)"}}>Date angajator</div>
      <div style={{fontSize:11,color:"#64748B",marginBottom:10}}>
        Ajung automat pe instalarea clientului, la activarea licenței — Adminul găsește angajatorul deja
        configurat. Denumirea și CUI-ul rămân blocate acolo; restul le poate completa/modifica Adminul.
      </div>
      <div style={{display:"grid",gap:10}}>
        <div><label className="label">Adresă sediu</label>
          <input className="input" value={value.employer_address||""} onChange={set("employer_address")} data-testid="client-employer-address" /></div>
        <div className="grid-2">
          <div><label className="label">Telefon</label>
            <input className="input" value={value.employer_phone||""} onChange={set("employer_phone")} data-testid="client-employer-phone" /></div>
          <div><label className="label">E-mail angajator</label>
            <input className="input" value={value.employer_email||""} onChange={set("employer_email")} data-testid="client-employer-email" /></div>
        </div>
        <div className="grid-2">
          <div><label className="label">Reprezentant legal — nume și prenume</label>
            <input className="input" value={value.legal_representative_name||""} onChange={set("legal_representative_name")} data-testid="client-legal-name" /></div>
          <div><label className="label">Reprezentant legal — funcția</label>
            <input className="input" placeholder="ex: Rector, Administrator" value={value.legal_representative_title||""} onChange={set("legal_representative_title")} data-testid="client-legal-title" /></div>
        </div>
        <div style={{fontSize:11,color:"#94A3B8"}}>Reprezentantul legal apare la „Aprobă" pe rapoartele PDF de programare CO.</div>
      </div>
    </div>
  );
}
